import { IVector3 } from '../../types';
import { ConfigParser } from '../ConfigParser';

export enum LootBuyerType {
	FISH = 'fish',
	FARM = 'farm',
	WOOD = 'wood',
	ORE = 'ore',
}

export class LootBuyersConfig {
	private readonly items: LootBuyersItemConfig[] = [];
	private readonly types: LootBuyersTypeConfig[] = [];

	public readonly priceUpdateIntervalHours: number;

	public readonly locations: LootBuyersLocationConfig[] = [];

	constructor(tables: Record<string, any>) {
		const rawCommon = tables['common'];
		this.priceUpdateIntervalHours = ConfigParser.getInt(rawCommon, 'price_update_interval_hours');

		const rawItems = tables['items'];
		for (const itemConfig of rawItems) {
			this.items.push(new LootBuyersItemConfig(itemConfig));
		}

		const rawTypes = tables['types'];
		for (const typeConfig of rawTypes) {
			const buyerType = ConfigParser.getEnum(typeConfig, 'buyer_type', LootBuyerType);
			const itemsConfigs = this.getItemsForType(buyerType);
			this.types.push(new LootBuyersTypeConfig(typeConfig, itemsConfigs));
		}

		const rawLocations = tables['locations'];
		for (const locationConfig of rawLocations) {
			this.locations.push(new LootBuyersLocationConfig(locationConfig));
		}
	}

	public getTypeConfigOrThrow(buyerType: LootBuyerType): LootBuyersTypeConfig {
		const typeConfig = this.types.find((type) => type.buyerType === buyerType);
		if (!typeConfig) {
			throw new Error(`Type config not found for buyerType: "${buyerType}"`);
		}

		return typeConfig;
	}

	private getItemsForType(buyerType: LootBuyerType): LootBuyersItemConfig[] {
		return this.items.filter((item) => item.buyerType === buyerType);
	}

	public getClientConfig() {
		return {
			types: this.types.map((e) => e.getClientConfig()),
			locations: this.locations.map((e) => e.getClientConfig()),
			items: this.items.map((e) => e.getClientConfig()),
		};
	}
}

export class LootBuyersTypeConfig {
	public readonly buyerType: LootBuyerType;
	public readonly blipSprite: number;
	public readonly blipColor: number;
	public readonly blipText: string;
	public readonly isEnabled: boolean;

	public readonly items: LootBuyersItemConfig[];

	constructor(config: Record<string, any>, itemsConfigs: LootBuyersItemConfig[]) {
		this.buyerType = ConfigParser.getEnum(config, 'buyer_type', LootBuyerType);
		this.blipSprite = ConfigParser.getInt(config, 'blip_sprite');
		this.blipColor = ConfigParser.getInt(config, 'blip_color');
		this.blipText = ConfigParser.getString(config, 'blip_text');
		this.isEnabled = ConfigParser.getBool(config, 'is_enabled');

		this.items = itemsConfigs;
	}

	public getClientConfig() {
		return {
			buyerType: this.buyerType,
			blipSprite: this.blipSprite,
			blipColor: this.blipColor,
			blipText: this.blipText,
			isEnabled: this.isEnabled,

			items: this.items.map((e) => e.getClientConfig()),
		};
	}
}

export class LootBuyersLocationConfig {
	public readonly id: string;
	public readonly buyerType: LootBuyerType;
	public readonly markerPosition: IVector3;
	public readonly pedPosition: IVector3;
	public readonly pedHeading: number;
	public readonly pedModel: string;

	constructor(config: Record<string, any>) {
		this.id = ConfigParser.getString(config, 'id');
		this.buyerType = ConfigParser.getEnum(config, 'buyer_type', LootBuyerType);
		this.markerPosition = ConfigParser.getVector3(config, 'marker_position');
		this.pedPosition = ConfigParser.getVector3(config, 'ped_position');
		this.pedHeading = ConfigParser.getFloat(config, 'ped_heading');
		this.pedModel = ConfigParser.getString(config, 'ped_model');
	}

	public getClientConfig() {
		return {
			id: this.id,
			buyerType: this.buyerType,
			markerPosition: this.markerPosition,
			pedPosition: this.pedPosition,
			pedHeading: this.pedHeading,
			pedModel: this.pedModel,
		};
	}
}

export class LootBuyersItemConfig {
	public readonly itemId: string;
	public readonly buyerType: LootBuyerType;
	public readonly priceMin: number;
	public readonly priceMax: number;

	constructor(config: Record<string, any>) {
		this.itemId = ConfigParser.getString(config, 'item_id');
		this.buyerType = ConfigParser.getEnum(config, 'buyer_type', LootBuyerType);
		this.priceMin = ConfigParser.getFloat(config, 'price_min');
		this.priceMax = ConfigParser.getFloat(config, 'price_max');
	}

	public getClientConfig() {
		return {
			itemId: this.itemId,
			buyerType: this.buyerType,
		};
	}
}
